import React, {useEffect, useState} from 'react';
import {
    Button,
    Col,
    Divider,
    Form,
    Input,
    InputNumber,
    Select,
    Typography,
    Row,
    Slider,
    Space,
    Tooltip,
    Checkbox,
    Tag, Modal
} from "antd";
import {InfoCircleOutlined} from "@ant-design/icons";
import {GLOBAL_CONFIG} from "../../tools/global_config";
import {track} from "@amplitude/analytics-browser";
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import DecimalStep from "../DecimalStep";
import {useNavigate} from "react-router-dom";
import {Switch} from "antd/es";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { CheckableTag } = Tag;

const tagsData = ['HD', 'Cyberpunk', 'Horror', 'Anime', 'Professional photo'];

const SourceParamsPanel = ({boosting, loading, selectedSource, onCreateClickCallback, onBoostPromptClicked, boostedPrompt, showTips}) => {

    const openInNewTab = url => {
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const navigate = useNavigate();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const showModalIfNeeded = () => {
        const key = "source_generate_click_counter";

        if (localStorage.getItem(key) !== null) {
            if (parseInt(localStorage.getItem(key)) === 4) {
                track("feedback_alert_shown");
                setIsModalOpen(true);
            }

            localStorage[key] = parseInt(localStorage[key]) + 1;
        } else {
            localStorage[key] = 1;
        }

        console.log(localStorage[key]);
    };

    const handleOk = () => {
        openInNewTab(GLOBAL_CONFIG.survey_url);
        setIsModalOpen(false);
    };

    const handleCancel = () => {
        setIsModalOpen(false);
    };

    const [selectedTag, setSelectedTag] = useState("");
    const [similarityValue, setSimilarityValue] = useState(0.3);
    const [depthValue, setDepthValue] = useState(true);
    const [paramsPanel, setParamsPanel] = useState(<div/>);

    const [form] = Form.useForm();

    const maxSimilarity = 1.0;
    const minSimilarity = 0.01;

    const marks = {};
    marks[maxSimilarity] = "Similar";
    marks[minSimilarity] = "Unlike";

    let initialValues = {
        prompt: "",
        keep_depth: depthValue,
        results: 3,
        steps: 50,
        similarity: similarityValue
    }

    useEffect(() => {
        let defaultPrompt = "";
        form.setFieldValue("prompt", defaultPrompt);
        setSelectedTag('');

        if (selectedSource.hasOwnProperty("default_prompt")) {
            defaultPrompt = selectedSource["default_prompt"];
            form.setFieldValue("prompt", defaultPrompt);
            setSelectedTag(tagsData[0]);
        }

        if (selectedSource.hasOwnProperty("params")) {
            let params = selectedSource.params;
            if (params.prompt.length > 0) {
                defaultPrompt = params.prompt;
            }
            form.setFieldValue("prompt", defaultPrompt);
            initialValues = {
                prompt: defaultPrompt,
                results: params.batch_size,
                steps: params.num_steps,
                similarity: 1.01 - params.strength
            }
            setSimilarityValue(1.01 - params.strength);

            if (params.prompt_style_idx > 0) {
                setSelectedTag(tagsData[params.prompt_style_idx - 1]);
            }

        }
    }, [selectedSource]);

    useEffect(() => {
        if (boostedPrompt.length) {
            initialValues.prompt = boostedPrompt;
            form.setFieldValue("prompt", boostedPrompt);
        }
    }, [boostedPrompt]);

    const promptField = (
        <Form.Item name="prompt" label="Prompt">
            <TextArea rows={3} />
        </Form.Item>
    );

    const tagsField = (
        <div style={{marginBottom: 16}}>
            {tagsData.map((tag) => (
                <CheckableTag
                    key={tag}
                    checked={selectedTag === tag}
                    onChange={(checked) => {
                        if (checked) {
                            setSelectedTag(tag);
                        } else {
                            setSelectedTag("");
                        }
                    }}
                >
                    {tag}
                </CheckableTag>
            ))}
        </div>
    );

    const boostButton = (
        <Button
            loading={boosting}
            htmlType="button"
            type="primary"
            onClick={() => onBoostPromptClicked(form.getFieldValue("prompt"))}
            style={{marginTop: -16, marginBottom: 16, width: 256, height: 40, borderRadius: 8}}
            disabled={selectedSource.hasOwnProperty("key") === false}
        >
            Go Crazy!
        </Button>
    );

    const resultsField = (
        <Form.Item name="results" label="Results">
            <InputNumber min={1} max={10} style={{width: "100%"}}/>
        </Form.Item>
    );

    const stepsField = (
        <Form.Item name="steps" label="Detailing">
            <Slider
                style={{width: "80%"}}
                min={40}
                max={80}
                step={1}
                tooltip={{open: false}}
            />
        </Form.Item>
    );

    const similarityField = (
        <Form.Item name="similarity" label="Similarity">
            <DecimalStep
                style={{width: "80%"}}
                minValue={minSimilarity}
                maxValue={maxSimilarity}
                marks={marks}
                decimalValue={similarityValue}
                setDecimalValue={setSimilarityValue}
            />
        </Form.Item>
    );

    const depthField = (
        <Form.Item name="keep_depth" label="Keep geometry">
            <Switch checked={depthValue} onChange={setDepthValue}/>
        </Form.Item>
    );

    return (
        <div style={{width: 440}}>
            <Title
                level={5}
                style={{marginTop: 16, marginBottom: 16, marginLeft: 16, float: "left"}}
            >
                Diffusion parameters
            </Title>
            <Divider />
            {
                selectedSource.hasOwnProperty("key")
                    ?
                    <Space
                        direction="vertical"
                        size="middle"
                        style={{
                            display: 'flex',
                        }}
                    >
                        <Text
                            strong
                            style={{margin: "auto", width: "50%"}}>
                            {selectedSource.name}
                        </Text>
                        <div style={{marginBottom: 16}}>
                            <a
                                href={selectedSource.url}
                                download={true}
                                onClick={() => track("download_clicked", {"path" : selectedSource.url})}
                            >
                                Download
                            </a>
                        </div>
                    </Space>
                    : <div/>
            }
            <Form
                form={form}
                labelCol={{
                    span: 6, offset: 1
                }}
                wrapperCol={{
                    span: 12, offset: 2
                }}
                labelAlign="right"
                layout="horizontal"
                initialValues={initialValues}
            >
                {
                    showTips
                        ? <Tooltip title="Description of the object(s), style, and color scheme of the desired output (affects the quality a lot)." placement="left">
                            {promptField}
                        </Tooltip>
                        : promptField
                }

                {
                    showTips
                        ? <Tooltip title="Type an object into the prompt line, select one of the predefined styles, and voila!" placement="left">
                            {tagsField}
                        </Tooltip>
                        : tagsField
                }

                <div style={{marginBottom: 16}}>
                    <Button type="link" onClick={() => {window.open("../prompt-guide", '_blank')}}>
                        Prompt guide
                    </Button>
                </div>

                {
                    showTips
                        ? <Tooltip
                            title="Your prompt will be augmented with unexpected and crazy styles. P.S. mixing it with predefined styles can lead to a quality decrease"
                            placement="left">
                            {boostButton}
                        </Tooltip>
                        : boostButton
                }

                {
                    showTips
                        ? <Tooltip title="Number of results to generate." placement="left" >
                            {resultsField}
                        </Tooltip>
                        : resultsField
                }

                {
                    showTips
                        ? <Tooltip title="Level of similarity between the source image and the outputs." placement="left" >
                            {similarityField}
                        </Tooltip>
                        : similarityField
                }

                {
                    showTips
                        ? <Tooltip title="Use image depth prediction for saving geometry." placement="left" >
                            {depthField}
                        </Tooltip>
                        : depthField
                }
            </Form>
            <Button
                loading={loading}
                type="primary"
                style={{width: 256, marginTop: 16, marginBottom: 32, height: 40, borderRadius: 8}}
                onClick={() => {
                    showModalIfNeeded();

                    let url = GLOBAL_CONFIG.sd_root_url
                    let params = {
                        input_image_blob_path: selectedSource.path.substring(selectedSource.path.indexOf('/') + 1),
                        batch_size: form.getFieldValue("results"),
                        keep_depth: depthValue,
                        prompt: form.getFieldValue("prompt"),
                        num_steps: form.getFieldValue("steps"),
                        strength: 1.01 - similarityValue,
                        prompt_style_idx: tagsData.includes(selectedTag) ? tagsData.indexOf(selectedTag) + 1 : 0,
                        is_default_image: selectedSource.hasOwnProperty("default_prompt"),
                        scale: 5,
                        ddim_eta: 1
                    }

                    const img = new Image();
                    img.src = selectedSource.url;

                    img.onload = () => {
                        track("aspect_ratio_source_generate", {ratio: img.width / img.height});
                    };

                    onCreateClickCallback(url, params)
                }}
                disabled={selectedSource.hasOwnProperty("key") === false}
            >
                Generate
            </Button>
            <Modal title="🚀 Feedback Request" open={isModalOpen} onOk={handleOk} onCancel={handleCancel} okText={"Take a survey"} cancelText={"Cancel"}>
                <Text style={{fontSize: 18}}>
                    Seems like you already has some experience with Dimensions. Great job! 🥳
                    <br/><br/>
                    Your <strong>feedback is crucial</strong> for our service to evolve.
                    <br/><br/>
                    Please, don't hesitate to join <a href="https://discord.gg/88tMErrSuq">our Discord server</a> and drop any message with your feedback.
                    <br/><br/>
                    Also, if you are a member of a studio, we will be more than glad to collaborate and develop a custom solution for you.
                    <br/>
                    Just <a href="mailto:dima@dimensions.team">drop us an email</a>.
                </Text>
            </Modal>
        </div>
    );
};

export default SourceParamsPanel;